import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'

import i18n from 'i18next'

import { ErrorFallback } from '@/components/ErrorFallback'
import { Toaster } from '@/components/ui/sonner'
import { env } from '@/env'
import { toI18nLanguage } from '@/locales/languagePreference'
import type { PinStatus } from '@/modules/graphql/codegen/graphql'
import { setAccessToken } from '@/modules/graphql/graphqlAuth'
import { whoAmIQueryOptions } from '@/modules/graphql/hooks/useWhoAmI'
import { logger } from '@/modules/utils'

import type { RouterContext } from '../App'

function RootComponent() {
  return (
    <div className="h-screen">
      <Outlet />
      <ReactQueryDevtools buttonPosition="bottom-left" />
      {env.VITE_TANSTACK_DEVTOOLS_ENABLED && (
        <TanStackRouterDevtools position="bottom-right" />
      )}
      <Toaster />
    </div>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  errorComponent: ErrorFallback,
  component: RootComponent,
  beforeLoad: async ({ context, location }) => {
    const loginRoute = '/callback'

    if (env.VITE_AUTH_ENABLED) {
      if (location.pathname === loginRoute) return

      if (!context.auth?.isAuthenticated) {
        throw redirect({
          to: loginRoute,
          // Seed a relative path (not the absolute href) so the same-origin
          // validator on the callback route accepts it, while still restoring
          // the user's original deep link after login.
          search: {
            redirect_to:
              window.location.pathname +
              window.location.search +
              window.location.hash,
          },
          replace: true,
        })
      }

      // Ensure the GraphQL client has the auth token before making requests.
      // useGraphqlAuth sets this in a useEffect (post-render), but beforeLoad
      // runs before that effect fires on initial navigation.
      setAccessToken(context.auth.user?.access_token ?? null)
    }

    let permissions: string[] = []
    let pinStatus: PinStatus | null = null

    if (env.VITE_DEV_PERMISSIONS) {
      // Runs without the backend, so whoAmI — and with it the language
      // preference — is skipped; the app stays in English in this dev mode.
      permissions = env.VITE_DEV_PERMISSIONS.split(',').map((p) => p.trim())
      console.info('Using dev permission overrides:', permissions)
    } else {
      try {
        const whoAmI =
          await context.queryClient.ensureQueryData(whoAmIQueryOptions)
        permissions = whoAmI?.permissions ?? []
        pinStatus = whoAmI?.pinStatus ?? null
        await i18n.changeLanguage(toI18nLanguage(whoAmI?.preferences?.language))
      } catch (error) {
        logger.error('Failed to load permissions:', error)
      }
    }

    return {
      permissions,
      pinStatus,
    }
  },
})
